import { ApiSync, ApiSyncStatus, ApiSyncType } from "@incendium/api";
import { normalizeStoreUrl } from "features/dataConnectors/utils/normalizeStoreUrl";

export const INCENDIUM_WP_PLUGIN_URL =
  "https://wordpress.org/plugins/incendium/";

export const INCENDIUM_WP_PLUGIN_SLUG = "incendium";

/** WordPress admin plugin install screen for Incendium on a connected store */
export function getWooCommercePluginInstallUrl(storeUrl: string): string {
  const base = normalizeStoreUrl(storeUrl);
  if (!base) {
    return INCENDIUM_WP_PLUGIN_URL;
  }
  try {
    const url = new URL(base);
    const pathPrefix =
      url.pathname === "/" ? "" : url.pathname.replace(/\/+$/, "");
    const params = new URLSearchParams({
      tab: "plugin-information",
      plugin: INCENDIUM_WP_PLUGIN_SLUG,
    });
    return `${url.origin}${pathPrefix}/wp-admin/plugin-install.php?${params.toString()}`;
  } catch {
    return `${base}/wp-admin/plugin-install.php?tab=plugin-information&plugin=${INCENDIUM_WP_PLUGIN_SLUG}`;
  }
}

const PLUGIN_INSTALL_POPUP = "incendium-woocommerce-plugin-install";

/** Opens the store's Incendium plugin page in a popup. Returns false if blocked. */
export function openWooCommercePluginInstaller(storeUrl: string): boolean {
  const installUrl = getWooCommercePluginInstallUrl(storeUrl);
  const popup = window.open(
    installUrl,
    PLUGIN_INSTALL_POPUP,
    "popup=yes,width=1100,height=800,noopener,noreferrer",
  );
  return popup != null;
}

export function resolveWooCommerceStoreUrl(
  syncs: ApiSync[] | undefined,
  fallbackDomain?: string,
): string {
  const wooSync = syncs?.find(
    (s) => s.type === ApiSyncType.WOOCOMMERCE_CONNECTOR,
  );
  if (wooSync) {
    const meta = parseWooStoreMeta(wooSync.client?.userMeta);
    if (meta?.store_url) {
      return normalizeStoreUrl(meta.store_url);
    }
    if (wooSync.account) {
      const account = wooSync.account;
      return normalizeStoreUrl(
        /^https?:\/\//i.test(account) ? account : `https://${account}`,
      );
    }
  }
  return fallbackDomain ? normalizeStoreUrl(fallbackDomain) : "";
}

export type WooPluginConnectionPhase =
  | "ready"
  | "init_pending"
  | "settings_pending"
  | "wc_inactive"
  | "plugin_unreachable"
  | "store_only"
  | "oauth_error";

export interface WooCommerceConnectionStatus {
  phase: WooPluginConnectionPhase;
  title: string;
  message?: string;
  showInstallInstructions: boolean;
}

export interface WooStorePluginMeta {
  name?: string;
  site_url?: string;
  wp_version?: string;
  wc_active?: boolean;
  php_version?: string;
  ping_ok_at_date?: string;
  init_ok_at_date?: string;
  last_error?: string;
}

export interface WooStoreMeta {
  store_url?: string;
  host?: string;
  name?: string;
  plugin?: WooStorePluginMeta;
  products_synced_at?: string;
  orders_synced_at?: string;
  orders_sync_next_page?: number;
}

export interface WooCommerceBackfillStatus {
  message: string;
}

export function parseWooStoreMeta(
  userMeta: string | undefined,
): WooStoreMeta | undefined {
  if (!userMeta) {
    return undefined;
  }
  try {
    return JSON.parse(userMeta) as WooStoreMeta;
  } catch {
    return undefined;
  }
}

export function getWooCommerceBackfillStatus(
  sync: ApiSync | undefined,
): WooCommerceBackfillStatus | null {
  if (!sync || sync.syncedAt) {
    return null;
  }

  if (sync.status === ApiSyncStatus.ERROR) {
    return null;
  }

  const meta = parseWooStoreMeta(sync.client?.userMeta);
  const plugin = meta?.plugin;

  if (!plugin?.ping_ok_at_date) {
    return null;
  }

  if (
    !plugin.init_ok_at_date &&
    !meta?.products_synced_at &&
    !meta?.orders_synced_at
  ) {
    return {
      message:
        "Your WooCommerce store is connected. Finishing setup before we fetch your data.",
    };
  }

  if (!meta?.products_synced_at) {
    return {
      message:
        "We're fetching products from your WooCommerce store. This may take a while.",
    };
  }

  if (!meta?.orders_synced_at) {
    return {
      message:
        "We're fetching orders from your WooCommerce store. This may take a while.",
    };
  }

  return {
    message:
      "We're finishing your WooCommerce store sync. Your data will appear shortly.",
  };
}

export function isWooCommercePluginDetected(
  sync: ApiSync | undefined,
): boolean {
  if (!sync) {
    return false;
  }
  const plugin = parseWooStoreMeta(sync.client?.userMeta)?.plugin;
  return !!plugin?.ping_ok_at_date;
}

export function getWooCommerceConnectionStatus(
  sync: ApiSync,
): WooCommerceConnectionStatus {
  const meta = parseWooStoreMeta(sync.client?.userMeta);
  const plugin = meta?.plugin;

  if (sync.status === ApiSyncStatus.PENDING) {
    return {
      phase: "settings_pending",
      title: "Applying settings",
      message:
        "Your changes have been saved and will be pushed to your store shortly (usually within 10 minutes).",
      showInstallInstructions: false,
    };
  }

  if (sync.status === ApiSyncStatus.ERROR && !plugin?.ping_ok_at_date) {
    return {
      phase: "oauth_error",
      title: "Store authorization failed",
      message:
        sync.error || plugin?.last_error || "Reconnect your store to continue.",
      showInstallInstructions: false,
    };
  }

  if (sync.syncedAt || plugin?.init_ok_at_date) {
    return {
      phase: "ready",
      title: "Connected",
      message:
        "The Incendium plugin is installed and your store is fully connected.",
      showInstallInstructions: false,
    };
  }

  if (plugin?.ping_ok_at_date && plugin.wc_active === false) {
    return {
      phase: "wc_inactive",
      title: "WooCommerce is not active",
      message:
        plugin.last_error ||
        "Activate WooCommerce on your WordPress site, then refresh this page.",
      showInstallInstructions: false,
    };
  }

  if (plugin?.ping_ok_at_date) {
    return {
      phase: "init_pending",
      title: "Plugin detected — finishing setup",
      message:
        plugin.last_error ||
        "We reached your store but have not finished plugin setup yet. This usually completes within a few minutes.",
      showInstallInstructions: false,
    };
  }

  if (plugin?.last_error) {
    return {
      phase: "plugin_unreachable",
      title: "Incendium plugin not reachable",
      message: plugin.last_error,
      showInstallInstructions: true,
    };
  }

  return {
    phase: "store_only",
    title: "Install the Incendium WordPress plugin",
    message:
      "Your store is authorized. Install and activate the Incendium plugin on WordPress—we will configure it automatically.",
    showInstallInstructions: true,
  };
}
